import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { LocaleDirection, LocaleType, StorageType } from '@core/enums';
import { StorageService } from '@core/services/storage.service';
import { environment as env } from '@environments/environment';
import * as Sentry from '@sentry/angular-ivy';
import { LocaleService } from '@shared/services/locale.service';
import { AuthService } from '@shared/services/auth.service';
import { AmplitudeService } from '../services/amplitude.service';
import { PlaceAdService } from '../services/place-ad.service';
import { SeoService } from '../services/seo.service';
import { SeoUtil } from '@core/utils';

/**
 * Provide the factory function in the index.ts of initializers folder.
 * We can have a separate file for every initializer if needed.
 */

/**
 * Amplitude
 */
export const initAmplitudeFactory = (amplitude: AmplitudeService, auth: AuthService) => {
	return () => {
		amplitude.init();

		// Check if the user is already logged-in, the set custom Amplitude user id
		if (auth.user()) {
			amplitude.setUserId(auth.user()?.phone);
		} else {
			// Make the user anonymous after logout
			amplitude.setUserId(null);
		}
	};
};

/**
 * SEO
 */
export const initSeoFactory = (seo: SeoService, router: Router) => {
	return () => {
		// Create canonical URL dynamically on route change
		router.events.subscribe((event: Event) => {
			if (event instanceof NavigationStart) {
				const URL = event.url.split('?')[0];

				// Remove the robots meta tag
				seo.removeRobotsMetaTag();

				// Remove the previous canonical link element as we just need one per page
				seo.removeCanonicalLinkElement();

				// Create acanonical URL if the route is included in the `sitemap.xml`
				if (SeoUtil.cannonicalRoutes.includes(URL)) {
					seo.createCanonicalLinkElement(URL);
				} else {
					seo.addRobotsMetaTag();
				}
			}
		});
	};
};

/**
 * Authentication
 */
export const initAuthFactory = (auth: AuthService, storageService: StorageService): any => {
	return () => {
		const user = storageService.getItem(StorageType.USER);

		if (user) {
			auth.setUser(JSON.parse(user));
		}
	};
};

/**
 * Place Ad
 */
export const initPlaceAdFactory = (placeAd: PlaceAdService, router: Router) => {
	return () => {
		/**
		 *  Enable only on home and ads page the place ad button fixed bottom
		 */
		router.events.subscribe((event: Event) => {
			if (event instanceof NavigationEnd) {
				const ROUTES = event.url.split('/');

				// TODO: We can enhance the ROUTES matcher by using regex.
				const IS_ADS_ROUTE = ROUTES.length === 4 && ROUTES[2] === 'c';
				const IS_HOME_ROUTE = ROUTES.length === 2 && ['en', 'ar', 'ku'].includes(ROUTES[1]);
				const IS_AD_ROUTE = ROUTES.length === 4 && ROUTES[2] === 'ad';

				placeAd.setEnablePlaceAdMobile(IS_ADS_ROUTE || IS_HOME_ROUTE || IS_AD_ROUTE);
			}
		});
	};
};

/**
 * Locale
 */
export const initLocaleFactory = (localeService: LocaleService, storageService: StorageService) => {
	return () => {
		const locale = storageService.getItem(StorageType.LOCALE);

		switch (locale) {
			case LocaleType.ARABIC:
			case LocaleType.KURDISTAN:
				localeService.setLocaleDirection(LocaleDirection.RTL);

				break;

			case LocaleType.ENGLISH:
				localeService.setLocaleDirection(LocaleDirection.LTR);

				break;
			default:
				localeService.setLocaleDirection(LocaleDirection.LTR);
		}
	};
};

/**
 * Sentry
 */
export const initSentryFactory = () => {
	return () => {
		Sentry.init({
			dsn: env.SENTRY.dsn,
			integrations: [
				new Sentry.BrowserTracing({
					// Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
					tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
					routingInstrumentation: Sentry.routingInstrumentation,
				}),
				new Sentry.Replay(),
			],
			// Performance Monitoring
			tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
			// Session Replay
			replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
			replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		});
	};
};
